import { useEffect, useRef, useState, memo } from "react";
import { Link } from "react-router-dom";

const products = [
  {
    id: "chair",
    category: "Chair",
    subtitle: "Ergonomic Design",
    description: "クリエイターの長時間作業を支える、人間工学に基づいた設計",
    image:
      "https://public.readdy.ai/ai/img_res/d3b424bc725ec9d7f3b374303e768510.jpg",
    link: "/chair/pro",
    badge: { type: "campaign", text: "CAMPAIGN" },
    price: 89800,
    originalPrice: 109800,
    priceLabel: "から",
  },
  {
    id: "desk",
    category: "Desk",
    subtitle: "Minimal Workspace",
    description: "必要なものだけを残した、究極のミニマルワークスペース",
    image:
      "https://readdy.ai/api/search-image?query=minimalist%20wooden%20desk%20in%20bright%20home%20office%20with%20laptop%20and%20creative%20tools%20designer%20working%20remotely%20natural%20daylight%20streaming%20through%20window%20plants%20and%20inspiring%20decor%20modern%20remote%20work%20setup%20professional%20photography%20warm%20atmosphere%20productive%20creative%20workspace%20contemporary%20interior%20aesthetic%20clean%20organized%20environment&width=600&height=800&seq=desk-remote-creator-001&orientation=portrait",
    link: "#desk-section",
    badge: { type: "new", text: "NEW" },
    price: 128000,
    priceLabel: "から",
  },
  {
    id: "gadget",
    category: "Gadget",
    subtitle: "Smart Accessories",
    description: "クリエイティブワークを加速する、スマートアクセサリー",
    image:
      "https://readdy.ai/api/search-image?query=digital%20artist%20using%20iPad%20Pro%20on%20sleek%20tablet%20stand%20drawing%20illustration%20with%20Apple%20Pencil%20creative%20workspace%20with%20art%20supplies%20and%20sketches%20in%20background%20warm%20studio%20lighting%20professional%20photography%20inspiring%20artistic%20environment%20modern%20illustration%20workspace%20aesthetic%20contemporary%20creative%20setup%20focused%20creative%20work%20atmosphere&width=600&height=800&seq=gadget-illustrator-creator-001&orientation=portrait",
    link: "/atelier-folio",
    badge: { type: "new", text: "COMING SOON" },
    price: 12800,
    priceLabel: "から",
  },
  {
    id: "tea",
    category: "Tea",
    subtitle: "Craft Tea Collab",
    description: "Craft Tea厳選のシングルオリジン日本茶・和紅茶をサブスクでお届け",
    image:
      "https://readdy.ai/api/search-image?query=creative%20professional%20taking%20tea%20break%20at%20home%20office%20desk%20with%20elegant%20Japanese%20tea%20cup%20and%20minimal%20white%20tea%20package%20laptop%20and%20creative%20work%20materials%20in%20background%20natural%20window%20light%20peaceful%20moment%20warm%20atmosphere%20professional%20photography%20mindful%20work-life%20balance%20aesthetic%20contemporary%20remote%20work%20lifestyle%20inspiring%20creative%20workspace&width=600&height=800&seq=tea-remote-creator-001&orientation=portrait",
    link: "/tea",
    badge: { type: "new", text: "COLLAB" },
    price: 1620,
    priceLabel: "から/月",
  },
];

const getBadgeStyles = (type: string) => {
  switch (type) {
    case "campaign":
      return "bg-[#C45C5C] text-white";
    case "new":
      return "bg-neutral-900 text-white";
    default:
      return "bg-neutral-800 text-white";
  }
};

const formatPrice = (value: number) => value.toLocaleString("ja-JP");

const ProductCard = memo(
  ({
    product,
    index,
    isVisible,
  }: {
    product: (typeof products)[0];
    index: number;
    isVisible: boolean;
  }) => {
    const [isHovered, setIsHovered] = useState(false);

    const isExternal = product.link.startsWith("http");
    const isAnchor = product.link.startsWith("#");

    const handleAnchorClick = (e: React.MouseEvent) => {
      if (isAnchor) {
        e.preventDefault();
        const element = document.querySelector(product.link);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }
    };

    const cardContent = (
      <div
        className={`group relative overflow-hidden transition-all duration-500 ease-out cursor-pointer ${
          isHovered ? "md:-translate-y-1" : ""
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image */}
        <div className="relative overflow-hidden rounded-xl aspect-[3/4] bg-neutral-100">
          <img
            src={product.image}
            alt={`${product.category} - ${product.subtitle}`}
            className={`w-full h-full object-cover object-top transition-all duration-700 ease-out ${
              isHovered ? "scale-105" : "scale-100"
            }`}
            loading="lazy"
            width="600"
            height="800"
          />

          {/* Hover overlay */}
          <div
            className={`absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent transition-opacity duration-500 ${
              isHovered ? "opacity-100" : "opacity-0"
            }`}
          />

          {/* Badge */}
          {product.badge && (
            <div className="absolute top-4 left-4 z-10">
              <span
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wider ${getBadgeStyles(
                  product.badge.type
                )}`}
              >
                {product.badge.type === "campaign" && (
                  <i className="ri-gift-line mr-1.5 text-xs" aria-hidden="true" />
                )}
                {product.badge.type === "new" && (
                  <i className="ri-sparkle-line mr-1.5 text-xs" aria-hidden="true" />
                )}
                {product.badge.text}
              </span>
            </div>
          )}

          {/* Hover CTA */}
          <div
            className={`absolute bottom-5 left-5 right-5 transition-all duration-500 ${
              isHovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <span className="inline-flex items-center gap-2 text-white text-xs font-medium tracking-[0.12em] uppercase">
              詳細を見る
              <span
                className={`w-7 h-7 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-transform duration-300 ${
                  isHovered ? "translate-x-1" : ""
                }`}
              >
                <i className="ri-arrow-right-line text-sm text-white" aria-hidden="true" />
              </span>
            </span>
          </div>
        </div>

        {/* Text content */}
        <div className="pt-5 pb-2">
          <p className="text-[10px] font-medium tracking-[0.25em] text-neutral-400 uppercase mb-1.5">
            {product.subtitle}
          </p>
          <h3 className="text-lg font-extralight tracking-[0.14em] text-neutral-900 uppercase mb-2">
            {product.category}
          </h3>

          <p className="text-[12.5px] font-light leading-relaxed text-neutral-500 mb-4 line-clamp-2">
            {product.description}
          </p>

          {/* Price */}
          <div className="flex items-baseline gap-2 flex-wrap">
            {product.originalPrice && (
              <span className="text-xs text-neutral-400 line-through">
                ¥{formatPrice(product.originalPrice)}
              </span>
            )}
            <span
              className={`text-base font-light tracking-tight ${
                product.originalPrice ? "text-[#C45C5C]" : "text-neutral-800"
              }`}
            >
              ¥{formatPrice(product.price)}
            </span>
            {product.priceLabel && (
              <span className="text-[11px] text-neutral-400">{product.priceLabel}</span>
            )}
            {product.originalPrice && (
              <span className="text-[10px] font-medium text-[#C45C5C] bg-[#C45C5C]/8 px-2 py-0.5 rounded-full whitespace-nowrap">
                {Math.round((1 - product.price / product.originalPrice) * 100)}% OFF
              </span>
            )}
          </div>
        </div>
      </div>
    );

    const wrapperClass = `transition-all duration-700 ${
      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
    }`;

    if (isExternal) {
      return (
        <article
          className={wrapperClass}
          style={{ transitionDelay: `${index * 120 + 200}ms` }}
          role="listitem"
        >
          <a
            href={product.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            aria-label={`${product.category}の詳細を見る`}
          >
            {cardContent}
          </a>
        </article>
      );
    }

    if (isAnchor) {
      return (
        <article
          className={wrapperClass}
          style={{ transitionDelay: `${index * 120 + 200}ms` }}
          role="listitem"
        >
          <a
            href={product.link}
            onClick={handleAnchorClick}
            className="block"
            aria-label={`${product.category}セクションへ移動`}
          >
            {cardContent}
          </a>
        </article>
      );
    }

    return (
      <article
        className={wrapperClass}
        style={{ transitionDelay: `${index * 120 + 200}ms` }}
        role="listitem"
      >
        <Link
          to={product.link}
          className="block"
          aria-label={`${product.category}の詳細ページへ`}
        >
          {cardContent}
        </Link>
      </article>
    );
  }
);

ProductCard.displayName = "ProductCard";

export default function ProductShowcase() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.08 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-20 sm:py-28 md:py-36 bg-[#FAFAF8] overflow-hidden"
      id="products"
      aria-labelledby="products-heading"
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div
          className={`mb-14 sm:mb-20 text-center transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-[11px] font-medium tracking-[0.25em] text-neutral-400 uppercase mb-3">
            Our Products
          </p>
          <h2
            id="products-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-extralight tracking-[0.08em] text-neutral-900 mb-5"
          >
            MeVER Products
          </h2>
          <div className="flex justify-center" aria-hidden="true">
            <div className="w-12 h-px bg-neutral-300" />
          </div>
        </div>

        {/* Equal 4-Column Grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          role="list"
        >
          {products.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>

        {/* Bottom line */}
        <div
          className={`mt-16 sm:mt-24 flex justify-center transition-all duration-1000 delay-700 ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden="true"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-px bg-neutral-300" />
            <span className="text-[11px] font-light tracking-[0.2em] text-neutral-400 uppercase">
              Explore All
            </span>
            <div className="w-10 h-px bg-neutral-300" />
          </div>
        </div>
      </div>
    </section>
  );
}
